import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Text, Box } from 'rebass';
import styled from '@emotion/styled';
import ImageNode from './ImageNode';
import VideoNode from './VideoNode';
import { useMemberName } from 'members/hooks/index';
import VideoPreviewContext from '../VideoPreviewContext';
import useEntity from 'entities/hooks/use-entity';
import TextLayer from 'app/payment/TextLayer';

export const LoadingBg = styled(Box)`
display: flex;
justify-content: center;
align-items: center;
background-color: rgba(0, 0, 0, 0.7);
animation-name: color;
animation-duration: 3s;
animation-iteration-count: infinite;
border-radius: 12px;
@keyframes color {
  0% {
    background-color: rgba(0, 0, 0, 0.7);
  }
  50% {
    background-color: rgba(0, 0, 0, 0.9);
  }
  100% {
    background-color: rgba(0, 0, 0, 0.7);
  }
}
`;

const findObjectById = (data, id) => {
  return Object.values(data).find(item => {
    return item.photo?.id === id
  });
};

function emailRender() {
  return (
    <span>
      <br />
      <a
        style={{ color: 'white', cursor: 'pointer' }}
        href={`mailto:help@babyyearbooks.com?subject=Error uploading a clip`}
      >
        help@babyyearbooks.com
      </a>
    </span>
  );
}

function findObjectByMonthId(data, targetId) {
  if (typeof data !== 'object' || data === null) return null;

  if (data.id === targetId) {
    return data;
  }

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const result = findObjectById(data[key], targetId);
      if (result) {
        return result;
      }
    }
  }

  return null;
}


function PreviewNode({
  currentTime,
  shouldPlay,
  onProgress,
  onPlay,
  onPause,
  onEnded,
  startAt,
  endAt,
  volume,
  id,
  height,
  width,
  setClipSoundtrackVolume,
  dispatch,
  setBuffering,
  isBuffering,
  isForEdit = false,
  originalVolume,
  clipDetail,
  handlePlayClick,
  handlePauseClick,
  monthsFrameLayersData = [],
  bookStyleMonth,
  index,
}) {
  const { currentClipId, currentOpacity } = useContext(VideoPreviewContext);

  const { entity: clip } = useEntity(clipDetail.image || clipDetail.video)
  const framLayersData = monthsFrameLayersData.filter(layerData => findObjectByMonthId(layerData, clip?.id));

  const uploadStatus = clip.state
  // const [opacity, setOpacity] = useState(0);
  const authorName = useMemberName((clip && clip?.['owner-id']) || {});

  const showLock = false
  // showLockIcon(celebration, clip);

  const normalizedVideo = useEntity(clip?.['normalized-media']);
  const nodeInRange = currentClipId === id;

  const [parentHeight, setParentHeight] = useState(null);
  const [posterSectionWidth, setPosterSectionWidth] = useState(0);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (posterSectionWidth && bookStyleMonth?.width) {
      const widthZoom = (posterSectionWidth / bookStyleMonth.width) * 100;
      const heightZoom = (parentHeight / bookStyleMonth.height) * 100;

      const zoomPercentage = Math.min(widthZoom, heightZoom);
      setZoom(zoomPercentage + 0.5);
    }
  }, [
    parentHeight,
    posterSectionWidth,
    bookStyleMonth?.width,
    bookStyleMonth?.height,
  ]);

  useEffect(() => {
    if (showLock) handlePauseClick();
  }, [showLock]);

  useEffect(() => {
    if (nodeInRange) {
      setClipSoundtrackVolume(
        typeof clipDetail['soundtrack-volume'] !== 'undefined'
          ? clipDetail['soundtrack-volume']
          : (clipDetail.type === 'Video' && 0.05) || 1
      );
    }
  }, [nodeInRange, setClipSoundtrackVolume, clipDetail]);

  if (uploadStatus === 'pending_upload')
    return (
      <LoadingBg sx={{ gridArea: '1 / 1 / 2 / 2' }}>
        <Text className="loading" color="white" textAlign="center" mt="30%">
          Processing
        </Text>
      </LoadingBg>
    );

  if (uploadStatus === 'stalled_upload' && nodeInRange)
    return (
      <LoadingBg sx={{ gridArea: '1 / 1 / 2 / 2' }}>
        <Text color="white" textAlign="center" m="10%">
          {authorName} tried to {clip.video ? 'upload/record a video' : 'upload a picture'}, but it
          did not reach us. Please ask them to try again
        </Text>
      </LoadingBg>
    );

  if (uploadStatus === 'error' && nodeInRange)
    return (
      <LoadingBg sx={{ pointerEvents: 'none', gridArea: '1 / 1 / 2 / 2', height }}>
        <Text color="white" fontSize="16px" textAlign="center" m="10%">
          {authorName
            ? `This file by ${authorName} cannot be processed. Please ask them to upload a different picture or video. If you need help, please contact 
          `
            : `File cannot be processed. Please upload a different picture or video. If you need help, please contact `}
          {emailRender()}
        </Text>
      </LoadingBg>
    );

  if (showLock)
    return (
      <LoadingBg sx={{ gridArea: '1 / 1 / 2 / 2' }}>
        <Flex
          height="100%"
          justifyContent="center"
          alignItems="center"
          backgroundColor="rgba(0, 0, 0, 0.7)"
        >
          <Text color="white" textAlign="center" m="10%" fontSize="30px">
            PayNow to see the preview
          </Text>
        </Flex>
      </LoadingBg>
    );

  const notNormalized = !normalizedVideo?.['hls-np'] || isForEdit;

  function getOpacity() {
    if (isForEdit || index === 1)
      return 1;
    if (nodeInRange) return currentOpacity;
    return 0;
  }

  return (
    <div
      style={{
        display: 'grid',
        backgroundColor: '#272929',
        // height: 'inherit',
        // width: 'inherit',
        opacity: getOpacity(),
        // overflowY: 'hidden',
        aspectRatio: '9 / 16',

        gridArea: '1 / 1 / 2 / 2',
        height: 'inherit',
        width: 'inherit',
        height: 'calc(100vh - 300px)', // Limit max height to 80% of the viewport height
        maxWidth: 'calc(100vh * 9 / 16)', // Maintain aspect ratio for portrait
        opacity: getOpacity(),
        overflowY: 'hidden',
        margin: 'auto',
      }}
      ref={(el) => {
        if (!el) return;
        setPosterSectionWidth(el.getBoundingClientRect().width);
        setParentHeight(el.getBoundingClientRect().height);
      }}
    >

      <Flex
        alignItems="stretch"
        justifyContent="center"
        sx={{ gridArea: '1 / 1 / 2 / 2', overflow: 'hidden', position: 'relative' }}
      >
        {clipDetail.video && (
          <VideoNode
            id={clip.id}
            normalizedVideo={normalizedVideo}
            index={index}
            clipDetail={clipDetail}
            currentTime={nodeInRange ? currentTime - startAt : 0}
            nodeInRange={nodeInRange}
            onPlay={onPlay}
            onPause={onPause}
            onStalled={onPause}
            volume={volume}
            onEnded={onEnded}
            shouldPlay={shouldPlay}
            onProgress={onProgress}
            startAt={startAt}
            originalVolume={originalVolume}
            endAt={endAt}
            isBuffering={isBuffering}
            setBuffering={setBuffering}
            isForEdit={isForEdit}
            handlePauseClick={handlePauseClick}
            handlePlayClick={handlePlayClick}
          />
        )}
        {(clipDetail.image && framLayersData.length > 0) ? <Box height="100%" width="100%"
          bg={bookStyleMonth?.['background-colour']}>
          {framLayersData.map((obj) => {
            return Object.keys(obj).map((id) => Object.keys(obj[id]).map((layerId) => {
              const mainObject = obj[id][layerId];
              const attributes = mainObject?.otherAttributes;

              return <Box width={`${attributes?.width}px`}
                height={`${attributes?.height}px`}
                sx={{
                  zoom: `${zoom}%`,
                  position: 'absolute',
                  top: `${attributes?.ypos}px`,
                  left: `${attributes?.xpos}px`,
                  zIndex: attributes?.['render-order']
                }}>

                {attributes?.type.includes("TextLayer") ?
                  <TextLayer
                    {...attributes}
                    zoom={zoom}
                    xpos="unset"
                    ypos="unset"
                    fontAlignment={attributes?.['font-alignment']}
                    fontFamily={attributes?.['font-name']}
                    fontWeight={attributes?.['font-weight']}
                    fontSize={attributes?.['font-size']}
                    color={attributes?.['font-colour']}
                  />
                  : <ImageNode
                    zoom={zoom}
                    id={clip.id}
                    attributes={attributes}
                    clipDetail={mainObject?.photo}
                    canvasImageDetails={mainObject?.canvasImage || {}}
                    framLayersData={framLayersData}
                    shouldPlay={shouldPlay}
                    nodeInRange={nodeInRange}
                    onPlay={onPlay}
                    onPause={onPause}
                    onEnded={onEnded}
                    height={height}
                    width={width}
                    dispatch={dispatch}
                    currentTime={currentTime - startAt}
                    setBuffering={setBuffering}
                    isBuffering={isBuffering}
                    isForEdit={isForEdit}
                  />
                }
              </Box>
            }))
          })}
        </Box>
          : null}
      </Flex>
    </div>
  );
}

PreviewNode.propTypes = {
  currentTime: PropTypes.number.isRequired,
  onPlay: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  endAt: PropTypes.number.isRequired,
  startAt: PropTypes.number.isRequired,
  shouldPlay: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
};

export default PreviewNode;
